import React, { useState, useEffect } from 'react';
import './DetailedInfo.css';
import CustomLink from './components/CustomLink'

const ClinicalTrials = ({ brandNames, activeSubstances }) => {
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchClinicalTrials = async () => {
      setLoading(true);
      setError(null);

      const excludedValues = ["No source available", "Unable to retrieve"];
      const activeSubstancesArray = Array.isArray(activeSubstances)
        ? activeSubstances
        : (activeSubstances ? [activeSubstances] : []);

      const uniqueTerms = Array.from(new Set(
        [...brandNames, ...activeSubstancesArray].filter(
          (term) => term && !excludedValues.includes(term)
        )
      ));

      const allQueries = uniqueTerms.map(term => `"${term}"`).join(' OR ');

      if (!allQueries) {
        setLoading(false);
        return;
      }

      const fetchData = async (query) => {
        const url = `https://clinicaltrials.gov/api/v2/studies?query.term=${encodeURIComponent(query)}&sort=@relevance`;
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response.json();
      };

      try {
        try {
          const data = await fetchData(allQueries);
          setStudies(data.studies || []);
        } catch (err) {
          // Retry with only brandNames if "Too complicated query" error
          if (err.message.includes("HTTP error!") && brandNames.length > 0) {
            const brandQuery = brandNames.map(term => `"${term}"`).join(' OR ');
            const data = await fetchData(brandQuery);
            setStudies(data.studies || []);
          } else {
            throw err;
          }
        }
      } catch (e) {
        setError("Failed to fetch clinical trial data.");
        console.error("Clinical trials API error:", e);
      } finally {
        setLoading(false);
      }
    };


    fetchClinicalTrials();
  }, [brandNames, activeSubstances]);

  const highlightText = (text, terms) => {
    if (!text || !terms || terms.length === 0) {
      return text;
    }
    let highlightedText = text;
    terms.forEach(term => {
      const regex = new RegExp(`\\b${term}\\b`, 'gi');
      highlightedText = highlightedText.replace(regex, `<span class="highlighted-word">$&</span>`);
    });
    return { __html: highlightedText };
  };

  if (loading) {
    return <p style={{ paddingLeft: '15px' }}>Loading clinical trials...</p>;
  }

  if (error) {
    return <p style={{ paddingLeft: '15px', color: 'red' }}>{error}</p>;
  }

  const excludedValues = ["No source available", "Unable to retrieve"];
  const activeSubstancesArray = Array.isArray(activeSubstances)
    ? activeSubstances
    : (activeSubstances ? [activeSubstances] : []);

  const uniqueTerms = Array.from(new Set(
    [...brandNames, ...activeSubstancesArray].filter(
      (term) => term && !excludedValues.includes(term)
    )
  ));

  const allQueries = uniqueTerms.map(term => `"${term}"`).join(' OR ');
  const searchUrl = `https://clinicaltrials.gov/search?term=${encodeURIComponent(allQueries)}`;

  if (studies.length === 0) {
    return <p style={{ paddingLeft: '15px' }}>No clinical trials found for this medication.</p>;
  }

  const searchTerms = [...brandNames, ...(Array.isArray(activeSubstances) ? activeSubstances : [activeSubstances])].filter(
    term => term && term !== "No source available" && term !== "Unable to retrieve"
  );

  return (
    <div className="med-content-container">
      <h1>Relevant Clinical Trials - <span style={{ color: 'orange' }}>ADMIN ONLY</span></h1>
      <div className="med-info-detail-group">
        <div className="med-info-detail-group-title">
          <CustomLink
            className="med-info-external-link"
            name="EU Clinical Trials Register (EudraCT) - See relevant trials"
            dest={`https://www.clinicaltrialsregister.eu/ctr-search/search?query=${encodeURIComponent(allQueries)}`}
          />
        </div>
      </div>
      <div className="med-info-detail-group">
        <div className="med-info-detail-group-title">
          <CustomLink
            className="med-info-external-link"
            name="EMA CTIS Public Portal – Open search page"
            dest={`https://euclinicaltrials.eu/search-clinical-trials-reports/`}
          />
        </div>
      </div>
      <div className="med-info-detail-group">
        <div className="med-info-detail-group-title">
          <h3>
            ClinicalTrials.gov - Relevant Clinical Trials{" "}
            <a href={searchUrl} target="_blank" rel="noopener noreferrer">
              (See All<i className="bx bx-link-external"></i>)
            </a>
          </h3>
        </div>
        <div className="clinical-trials-container">
          {studies.map(study => {
            const interventions = Array.from(new Set(
              (study.protocolSection.armsInterventionsModule?.interventions || []).map(intervention => intervention.name)
            )).join(', ') || 'N/A';

            return (
              <li key={study.protocolSection.identificationModule.nctId}>
                <a
                  href={`https://clinicaltrials.gov/ct2/show/${study.protocolSection.identificationModule.nctId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {study.protocolSection.identificationModule.briefTitle}
                  <i className="bx bx-link-external"></i>
                </a>
                <div className="study-details">
                  <p>
                    <strong>Trial Number:</strong> {study.protocolSection.identificationModule.nctId}{" "}
                    <strong>Status:</strong> {study.protocolSection.statusModule.overallStatus}{" "}
                    <strong>Start Date:</strong> {study.protocolSection.statusModule.startDateStruct?.date || 'N/A'}{" "}
                    <strong>Completion Date:</strong> {study.protocolSection.statusModule.completionDateStruct?.date || 'N/A'}{" "}
                  </p>
                  <p>
                    <strong>Type:</strong> {study.protocolSection.designModule.studyType || 'N/A'}{" "}
                    <strong>Phase:</strong> {study.protocolSection.designModule.phases?.join(', ') || 'N/A'}{" "}
                    <strong>Conditions:</strong> {study.protocolSection.conditionsModule.conditions?.join(', ') || 'N/A'}{" "}
                  </p>
                  <p>
                    <strong>Intervention:</strong> {interventions}
                  </p>
                  <p><strong>Sponsor:</strong> {study.protocolSection.sponsorCollaboratorsModule.leadSponsor?.name || 'N/A'}</p>
                  <strong>Description:</strong>
                </div>
                <p dangerouslySetInnerHTML={highlightText(study.protocolSection.descriptionModule?.briefSummary, searchTerms)}></p>
              </li>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ClinicalTrials;